import React, { useState, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  InputGroup,
  Card,
  Pagination,
} from "react-bootstrap";
import BackArrow from "../../../components/backArrow";
import "./hiremarketer.css";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { getAsset } from "../../../helpers/helper";
import { useGetMarketersByBusinessTypeQuery } from "../../../features/marketing/marketingApiSlice";
import PersonIcon from "@mui/icons-material/Person";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { encodeId } from "../../../utils/idObfuscator";
import { marketingTypeMap } from "../../../hooks/useBecomeAgent";

const avatarFallback = (name) =>
  `https://ui-avatars.com/api/?background=e9ecef&color=6c757d&bold=true&size=128&name=${encodeURIComponent(
    name || "User",
  )}`;

/** Reverse of marketingTypeMap: retailerShop → Retailer */
const industryKeyToLabel = Object.entries(marketingTypeMap).reduce(
  (acc, [label, key]) => {
    acc[String(key).toLowerCase()] = label;
    return acc;
  },
  {},
);

/** Resolve user profile photo whether imageUrl is a string, array, or object. */
const resolveMarketerAvatar = (marketer) => {
  const name = marketer?.userId?.fullname || "User";
  const raw = marketer?.userId?.imageUrl;
  let path = "";
  if (Array.isArray(raw)) {
    path = raw.find(Boolean) || "";
  } else if (raw && typeof raw === "object") {
    path = raw.url || raw.path || raw.src || "";
  } else if (typeof raw === "string") {
    path = raw;
  }
  path = String(path || "").trim();
  if (!path || path === "null" || path === "undefined") {
    return avatarFallback(name);
  }
  if (
    /^https?:\/\//i.test(path) ||
    path.startsWith("data:") ||
    path.startsWith("blob:")
  ) {
    return path;
  }
  return getAsset(path) || avatarFallback(name);
};

/** Marketing expert industry label (not category). */
const resolveExpertIndustryLabel = (marketer) => {
  const list = Array.isArray(marketer?.MIndustryName)
    ? marketer.MIndustryName
    : [];
  const raw = list
    .map((ind) =>
      typeof ind === "string" ? ind : ind?.name || ind?.businessType || "",
    )
    .map((s) => String(s || "").trim())
    .find(Boolean);
  if (!raw) return "—";
  const mapped = industryKeyToLabel[raw.toLowerCase()];
  if (mapped) return mapped;
  if (/[A-Z\s&]/.test(raw) && !/Shop$|Item$/i.test(raw)) return raw;
  return (
    raw.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/Shop$/i, "").trim() || raw
  );
};

export const setHireMarketerContext = ({ businessType, shopId } = {}) => {
  if (businessType) {
    localStorage.setItem("BTtogetMarketer", businessType);
  }
  if (shopId) {
    const sId = String(shopId);
    localStorage.setItem("shopid", sId);
    localStorage.setItem("OpenShopIdForMarketingByMarketer", sId);
  }
};

const HireMarketer = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const marketersPerPage = 8;
  const navigate = useNavigate();

  const businessType = localStorage.getItem("BTtogetMarketer");
  // The specific shop the user is currently viewing/hiring for — used so the
  // marketer's zone is matched against THIS shop's city, not just any shop
  // the user owns that happens to share the same business type.
  const shopId =
    localStorage.getItem("shopid") ||
    localStorage.getItem("OpenShopIdForMarketingByMarketer");

  const {
    data: responseData,
    isLoading,
    isError,
    error,
  } = useGetMarketersByBusinessTypeQuery(
    { businessType, shopId },
    {
      skip: !businessType,
    },
  );

  const marketers = useMemo(
    () => responseData?.data || [],
    [responseData?.data],
  );
  const resolvedZoneName =
    responseData?.userResolvedZone?.zoneName ||
    responseData?.userResolvedZone?.city ||
    null;

  const filteredMarketers = useMemo(() => {
    return marketers.filter((item) => {
      const status = String(item?.Status || "").toLowerCase();
      if (status !== "approved") {
        return false;
      }
      if (item?.submitDoc === false || item?.connection === false) {
        return false;
      }

      const name = item?.userId?.fullname?.toLowerCase() || "";
      const id = item?.marketerId?.toLowerCase() || "";
      const search = searchTerm.toLowerCase();

      return name.includes(search) || id.includes(search);
    });
  }, [marketers, searchTerm]);

  const indexOfLast = currentPage * marketersPerPage;
  const indexOfFirst = indexOfLast - marketersPerPage;
  const currentMarketers = filteredMarketers.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredMarketers.length / marketersPerPage);

  const EmptyState = ({ message, subMessage }) => (
    <Row
      className="justify-content-center align-items-center"
      style={{ minHeight: "300px" }}
    >
      <Col xs={12} className="text-center">
        <PersonIcon
          style={{ fontSize: "80px", color: "#ccc", marginBottom: "20px" }}
        />
        <h5 className="text-muted" style={{ marginBottom: "10px" }}>
          {message}
        </h5>
        <p className="text-secondary">
          {subMessage ||
            (message === "No marketers found"
              ? "Try adjusting your search criteria"
              : "No marketers available for this category")}
        </p>
      </Col>
    </Row>
  );

  const ErrorState = ({ error: err }) => (
    <Row
      className="justify-content-center align-items-center"
      style={{ minHeight: "300px" }}
    >
      <Col xs={12} className="text-center">
        <ErrorOutlineIcon
          style={{ fontSize: "80px", color: "#dc3545", marginBottom: "20px" }}
        />
        <h5 className="text-danger" style={{ marginBottom: "10px" }}>
          Error Loading Marketers
        </h5>
        <p className="text-secondary">
          {err?.data?.message ||
            err?.message ||
            "Failed to load marketers. Please try again later."}
        </p>
      </Col>
    </Row>
  );

  const SkeletonCard = () => (
    <Card className="marketer-card skeleton-card">
      <Card.Body className="p-4 text-center">
        <div className="skeleton-avatar mb-3">
          <div className="skeleton-circle" />
        </div>
        <div className="skeleton-text mb-2">
          <div
            className="skeleton-line skeleton-line-sm"
            style={{ width: "70%", margin: "0 auto" }}
          />
        </div>
        <div className="skeleton-text mb-2">
          <div
            className="skeleton-line skeleton-line-xs"
            style={{ width: "50%", margin: "0 auto" }}
          />
        </div>
        <div className="skeleton-text mb-3">
          <div
            className="skeleton-line skeleton-line-xs"
            style={{ width: "65%", margin: "0 auto" }}
          />
        </div>
        <div className="skeleton-text mb-2">
          <div
            className="skeleton-line skeleton-line-xs"
            style={{ width: "80%", margin: "0 auto" }}
          />
        </div>
        <div className="skeleton-text mb-4">
          <div
            className="skeleton-line skeleton-line-xs"
            style={{ width: "75%", margin: "0 auto" }}
          />
        </div>
        <div className="skeleton-button">
          <div
            className="skeleton-line"
            style={{ width: "100%", height: "38px", borderRadius: "6px" }}
          />
        </div>
      </Card.Body>
    </Card>
  );

  if (!businessType) {
    return (
      <Container>
        <Row>
          <div className="screenHeight">
            <div className="hire-marketer-page rounded">
              <Container className="py-4">
                <div className="mb-3">
                  <BackArrow />
                </div>
                <h2 className="title mb-4">Find a Marketer</h2>
                <EmptyState
                  message="Open hire from your shop profile"
                  subMessage="Go to your shop, service, or company profile and tap Hire so we can match marketers for that industry and zone."
                />
              </Container>
            </div>
          </div>
        </Row>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container>
        <Row>
          <div className="screenHeight">
            <div className="hire-marketer-page rounded">
              <Container className="py-4">
                <div className="mb-3">
                  <BackArrow />
                </div>
                <h2 className="title mb-4">Find a Marketer</h2>
                <div className="search-filter-wrapper mb-4">
                  <div className="search-bar-wrapper">
                    <InputGroup className="search-input-group">
                      <InputGroup.Text className="search-icon-bg">
                        <SearchIcon className="search-icon" />
                      </InputGroup.Text>
                      <Form.Control
                        type="text"
                        placeholder="Search by name or User ID"
                        className="search-input"
                        disabled
                      />
                    </InputGroup>
                  </div>
                </div>
                <Row className="g-3 g-md-4">
                  {[...Array(8)].map((_, index) => (
                    <Col key={index} xs={6} sm={6} md={4} lg={4} xl={3}>
                      <SkeletonCard />
                    </Col>
                  ))}
                </Row>
              </Container>
            </div>
          </div>
        </Row>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>
        <Row>
          <div className="screenHeight">
            <div className="hire-marketer-page rounded">
              <Container className="py-4">
                <div className="mb-3">
                  <BackArrow />
                </div>
                <h2 className="title mb-4">Find a Marketer</h2>
                <ErrorState error={error} />
              </Container>
            </div>
          </div>
        </Row>
      </Container>
    );
  }

  const shopIndustryLabel =
    industryKeyToLabel[String(businessType || "").toLowerCase()] ||
    businessType;

  return (
    <Container>
      <Row>
        <div className="screenHeight">
          <div className="hire-marketer-page rounded">
            <Container className="py-4">
              <div className="mb-3">
                <BackArrow />
              </div>

              <h2 className="title mb-1">Find a Marketer</h2>
              <p className="text-secondary mb-1" style={{ fontSize: "14px" }}>
                Marketing expert for: <strong>{shopIndustryLabel}</strong>
                {resolvedZoneName ? (
                  <>
                    {" "}
                    · same zone as your shop (
                    <strong>{resolvedZoneName}</strong>)
                  </>
                ) : null}
              </p>
              <div className="mb-4" />

              <div className="search-filter-wrapper mb-4">
                <div className="search-bar-wrapper">
                  <InputGroup className="search-input-group">
                    <InputGroup.Text className="search-icon-bg">
                      <SearchIcon className="search-icon" />
                    </InputGroup.Text>

                    <Form.Control
                      type="text"
                      placeholder="Search by name or User ID"
                      className="search-input"
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </InputGroup>
                </div>
              </div>

              {marketers.length === 0 && !resolvedZoneName && (
                <EmptyState
                  message="Unable to determine your zone"
                  subMessage="We couldn't resolve a zone for your shop or profile, so we can't match marketers yet. Please update your shop/profile city."
                />
              )}
              {marketers.length === 0 && resolvedZoneName && (
                <EmptyState
                  message="No marketers available"
                  subMessage={`No approved ${shopIndustryLabel} marketers found in the ${resolvedZoneName} zone yet.`}
                />
              )}

              {marketers.length > 0 && filteredMarketers.length === 0 && (
                <EmptyState message="No marketers found" />
              )}

              {filteredMarketers.length > 0 && (
                <>
                  <Row className="g-3 g-md-4">
                    {currentMarketers.map((marketer) => (
                      <Col
                        key={marketer._id}
                        xs={6}
                        sm={6}
                        md={4}
                        lg={4}
                        xl={3}
                      >
                        <Card className="marketer-card">
                          <Card.Body className="p-3 text-center">
                            <div className="profile-img mb-3">
                              <img
                                className="img-placeholder"
                                src={resolveMarketerAvatar(marketer)}
                                alt={marketer?.userId?.fullname || "Marketer"}
                                referrerPolicy="no-referrer"
                                onError={(e) => {
                                  const fallback = avatarFallback(
                                    marketer?.userId?.fullname || "User",
                                  );
                                  if (e.currentTarget.src !== fallback) {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = fallback;
                                  }
                                }}
                              />
                            </div>

                            <h6 className="name mb-1">
                              {marketer?.userId?.fullname}
                            </h6>
                            {marketer?.Status === "approved" &&
                              marketer?.submitDoc !== false &&
                              marketer?.connection !== false && (
                                <div
                                  className="text-success mb-1"
                                  style={{
                                    fontSize: "0.85rem",
                                    fontWeight: "600",
                                  }}
                                >
                                  ✓ Approved Marketer
                                </div>
                              )}
                            {marketer?.MCity && (
                              <div
                                className="text-muted mb-2"
                                style={{ fontSize: "0.75rem" }}
                              >
                                Zone: {marketer.MCity}
                              </div>
                            )}
                            <p className="expertise-label mb-2">
                              Marketing expert for:
                              <br />
                              <span className="expertise">
                                {resolveExpertIndustryLabel(marketer)}
                              </span>
                            </p>

                            <button
                              className="view-details-btn"
                              onClick={() =>
                                navigate(
                                  `/view-marketer-profile/${encodeId(
                                    marketer?.marketerId || marketer?._id,
                                  )}`,
                                )
                              }
                            >
                              View Details
                            </button>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>

                  {totalPages > 1 && (
                    <div className="pagination-wrapper">
                      <Pagination>
                        <Pagination.Prev
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage((prev) => prev - 1)}
                        />

                        {[...Array(totalPages)].map((_, index) => (
                          <Pagination.Item
                            key={index}
                            active={currentPage === index + 1}
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </Pagination.Item>
                        ))}

                        <Pagination.Next
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage((prev) => prev + 1)}
                        />
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </Container>
          </div>
        </div>
      </Row>
    </Container>
  );
};

export default HireMarketer;
